import React, { Fragment, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Switch,
  Typography
} from '@mui/material';
import xor from 'lodash/xor';
import { CompanyFeature, CompanyFeatureType } from 'types/companies.types';
import { toLabelCase } from 'utils/companyUtils';
import { useFeatureList } from 'components/Companies/hooks/useFeatureList';
import { useUpdateCompany } from 'components/Companies/hooks/useUpdateCompany';
import BooleanValueIndicator from 'components/shared/BooleanValueIndicator/BooleanValueIndicator';
import LabelWithValue from 'components/shared/LabelWithValue/LabelWithValue';
import { Label } from 'components/shared/LabelWithValue/styles';
import { StyledCheckbox, StyledContainer, StyledGrid } from '../styles';

interface Props {
  companyId: string;
  features: CompanyFeatureType[];
  useDefaultMapboxKey: boolean;
}

const EDITABLE_FEATURES: CompanyFeatureType[] = [
  CompanyFeature.FEEDBACK,
  CompanyFeature.CERTIFICATES,
  CompanyFeature.WHITE_LABELING,
  CompanyFeature.VERIFIED_PAYMENTS,
];

const CompanyFeatures: React.FC<Props> = ({ companyId, features, useDefaultMapboxKey }) => {
  const [isEditing, setIsEditing] = useState(false);
  const { companyFeatures, loading } = useFeatureList();
  const { updateCompanyFeatures } = useUpdateCompany({
    onSuccess: () => {
      setIsEditing(false);
    }
  });

  const [activeFeatures, setActiveFeatures] = useState<string[]>(
    features || []
  );
  const [useDefaultMapbox, setUseDefaultMapbox] = useState(useDefaultMapboxKey);

  if (loading) {
    return (
      <StyledContainer
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <CircularProgress color="secondary" />
      </StyledContainer>
    );
  }

  if (!companyFeatures) {
    return (
      <StyledContainer>
        <Alert severity="warning">Could not find any company features</Alert>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      <Box display="flex" justifyContent="space-between">
        <Typography gutterBottom fontWeight={700} variant="subtitle1">
          Features
        </Typography>
        <div>
          {isEditing && (
            <Button size="small" onClick={() => setIsEditing(!isEditing)}>
              Cancel
            </Button>
          )}
          <Button
            color="secondary"
            size="small"
            onClick={() => {
              if (isEditing) {
                updateCompanyFeatures({
                  variables: {
                    id: companyId,
                    input: {
                      features: activeFeatures,
                      useDefaultMapboxKey: useDefaultMapbox,
                    }
                  }
                });
              } else {
                setIsEditing(true);
              }
            }}
          >
            {isEditing ? 'Save' : 'Edit features'}
          </Button>
        </div>
      </Box>

      <StyledGrid container={true} spacing={3}>
        {isEditing ? (
          <Fragment>
            {EDITABLE_FEATURES.map(feature => {
              return (
                <Grid item key={`checkbox-${feature}`}>
                  <Label>{toLabelCase(feature)}</Label>
                  <StyledCheckbox
                    checked={activeFeatures.includes(feature)}
                    onChange={() => {
                      // toggles the selected feature on/off in the activeFeatures array.
                      setActiveFeatures(xor(activeFeatures, [feature]));
                    }}
                  />
                </Grid>
              );
            })}
          </Fragment>
        ) : (
          <Fragment>
            {companyFeatures.map(feature => {
              return (
                <LabelWithValue
                  key={`company-feature-${feature}`}
                  label={toLabelCase(feature)}
                >
                  <BooleanValueIndicator value={features.includes(feature)} />
                </LabelWithValue>
              );
            })}
          </Fragment>
        )}
      </StyledGrid>

      <Box mt={2}>
        <FormControlLabel
          control={
            <Switch
              checked={useDefaultMapbox}
              disabled={!isEditing}
              onChange={e => setUseDefaultMapbox(e.target.checked)}
            />
          }
          label="Use default Mapbox key"
        />
        <Typography color="text.secondary" display="block" variant="caption">
          When off, the company must provide their own Mapbox API token in Integration Settings.
        </Typography>
      </Box>
    </StyledContainer>
  );
};

export default CompanyFeatures;
